import { useRef, useState, useEffect } from 'react';
import { ProductCard } from './ProductCard.jsx';
import { SkeletonCard } from '../ui/Skeleton.jsx';
import { cn } from '../../lib/format.js';

/**
 * A horizontally scrolling row of products.
 *
 * Native scroll with snap points rather than a JavaScript carousel: it works
 * with a trackpad, a touchscreen, and a keyboard without any of them being a
 * special case, and it costs nothing on the main thread. The arrows are a
 * convenience for mouse users and hide themselves when there is nothing to
 * scroll to.
 */

export const ProductRail = ({ products, isPending, onQuickAdd, className }) => {
  const trackRef = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      setEdges({
        start: scrollLeft <= 4,
        end: scrollLeft + clientWidth >= scrollWidth - 4,
      });
    };

    update();
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      track.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [products]);

  const scrollBy = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <div className={cn('relative', className)}>
      <ul
        ref={trackRef}
        className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {isPending
          ? Array.from({ length: 4 }, (_, index) => (
              <li key={index} className="w-[44vw] shrink-0 snap-start sm:w-64">
                <SkeletonCard />
              </li>
            ))
          : products?.map((product, index) => (
              <li key={product._id} className="w-[44vw] shrink-0 snap-start sm:w-64">
                <ProductCard product={product} priority={index < 2} onQuickAdd={onQuickAdd} />
              </li>
            ))}
      </ul>

      {!isPending && products?.length > 3 && (
        <div className="mt-4 hidden justify-end gap-2 lg:flex">
          <RailButton direction={-1} disabled={edges.start} onClick={scrollBy} label="Scroll left" />
          <RailButton direction={1} disabled={edges.end} onClick={scrollBy} label="Scroll right" />
        </div>
      )}
    </div>
  );
};

const RailButton = ({ direction, disabled, onClick, label }) => (
  <button
    type="button"
    onClick={() => onClick(direction)}
    disabled={disabled}
    aria-label={label}
    className="grid size-9 place-items-center rounded border border-mist text-ink transition-colors hover:border-ink disabled:opacity-30 disabled:hover:border-mist focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
  >
    <svg viewBox="0 0 16 16" className={cn('size-4', direction === -1 && 'rotate-180')} fill="currentColor" aria-hidden="true">
      <path d="M6.2 3.2 5.14 4.26 8.88 8l-3.74 3.74L6.2 12.8 11 8z" />
    </svg>
  </button>
);

export default ProductRail;
